import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

// Generic single-wheel value picker, anchored to a tapped scorecard cell.
// Reuses the exact scroll-wheel look + gesture as the round scorecard's
// score entry (the .cg-score-pick* CSS) so editing an imported course feels
// identical to entering a score. Used on the course-verify screen for Par,
// Stroke Index, Slope and Rating — bounded option lists where a flick-wheel
// reads more naturally than a native <select>. Free-form numbers (yardage)
// stay as inline inputs so the native numeric keyboard is available.

export interface PickerOption {
  value: number;
  label: string;
}

interface Props {
  options: PickerOption[];
  value: number | undefined;
  label: string;
  anchorRect: DOMRect;
  onPick: (value: number) => void;
  onClose: () => void;
}

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 3;
const WHEEL_WIDTH = 72;
const PADDING_X = 12;
const LABEL_HEIGHT = 20;

export default function CourseValuePicker({
  options,
  value,
  label,
  anchorRect,
  onPick,
  onClose,
}: Props) {
  // Index the wheel starts on — the current value, or the first item when
  // the value is missing (blank cell) or not one of the options.
  const startIdx = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const [idx, setIdx] = useState(startIdx);
  const idxRef = useRef(idx);
  idxRef.current = idx;

  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const armedRef = useRef(false);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = startIdx * ITEM_HEIGHT;
    const t = setTimeout(() => {
      armedRef.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const next = Math.round(el.scrollTop / ITEM_HEIGHT);
        const clamped = Math.max(0, Math.min(options.length - 1, next));
        if (clamped !== idxRef.current) setIdx(clamped);
      }, 80);
    };
    el.addEventListener("scroll", handle, { passive: true });
    return () => {
      el.removeEventListener("scroll", handle);
      if (timer) clearTimeout(timer);
    };
  }, [options]);

  const commitAndClose = useCallback(() => {
    const opt = options[idxRef.current];
    if (opt) onPick(opt.value);
    onClose();
  }, [options, onPick, onClose]);

  const onItemTap = useCallback(
    (tapIdx: number) => {
      idxRef.current = tapIdx;
      const opt = options[tapIdx];
      if (opt) onPick(opt.value);
      onClose();
    },
    [options, onPick, onClose],
  );

  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!armedRef.current) return;
      const target = e.target as Node;
      if (overlayRef.current && overlayRef.current.contains(target)) return;
      commitAndClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "Enter") commitAndClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [commitAndClose, onClose]);

  const wheelH = VISIBLE_COUNT * ITEM_HEIGHT;
  const pickerW = WHEEL_WIDTH + PADDING_X * 2;
  const pickerH = wheelH + LABEL_HEIGHT + PADDING_X * 2;
  const cellCenterX = anchorRect.left + anchorRect.width / 2;
  const cellCenterY = anchorRect.top + anchorRect.height / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = cellCenterX - pickerW / 2;
  let top = cellCenterY - pickerH / 2;
  if (left < 4) left = 4;
  if (left + pickerW > vw - 4) left = vw - 4 - pickerW;
  if (top < 4) top = 4;
  if (top + pickerH > vh - 4) top = vh - 4 - pickerH;

  const current = options[idx]?.value;

  return (
    <div className="cg-score-pick-backdrop" aria-hidden>
      <div
        ref={overlayRef}
        className="cg-score-pick"
        role="dialog"
        aria-label={label}
        style={{ left, top, width: pickerW, height: pickerH }}
      >
        <div className="cg-score-pick-cols">
          <div className="cg-score-pick-col" style={{ width: WHEEL_WIDTH }}>
            <div className="cg-score-pick-label">{label}</div>
            <div
              className="cg-score-pick-wheel"
              style={{ height: wheelH, width: WHEEL_WIDTH }}
            >
              <div
                className="cg-score-pick-indicator"
                style={{ top: ITEM_HEIGHT, height: ITEM_HEIGHT }}
              />
              <div
                ref={scrollerRef}
                className="cg-score-pick-scroller"
                style={{ height: wheelH }}
              >
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
                {options.map((o, i) => (
                  <div
                    key={o.value}
                    className={`cg-score-pick-item${
                      o.value === current ? " cg-score-pick-item-on" : ""
                    }`}
                    style={{ height: ITEM_HEIGHT }}
                    onClick={() => onItemTap(i)}
                  >
                    {o.label}
                  </div>
                ))}
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
